import { Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { arc as arcBuilder } from "d3-shape"
import React from "react"
import useId from "react-use-uuid"

import { GradientDefs } from "react-vis"
import styled from "styled-components"

const ChartWrap = styled.div`
  position: relative;
  margin: 0 auto;
  padding: 0.5rem 0;
`

const CenterLabel = styled.div<{ color: string; sizeMultiplier: number }>`
  position: absolute;
  width: 100%;
  left: 0;
  text-align: center;
  top: 0;
  height: 100%;
  width: 100%;
  color: ${p => p.color};
  font-size: ${p => p.sizeMultiplier * 2}rem;
  display: flex;
  align-items: center;
  justify-content: center;

  > span {
    font-size: ${p => p.sizeMultiplier}rem;
    position: relative;
    left: 2px;
    top: ${p => p.sizeMultiplier * 0.4}rem;
    width: 10px;
  }
`

const TitleLabel = styled.div`
  padding: 0.5rem 0 0;
  text-align: center;
`

const PlotWrap = styled.div`
  position: relative;
`

interface IRadialProgressProps {
  color?: string | TChromaCssFunc | null | any
  /**
   * The max value on the scale
   * @default 100
   */
  max?: number
  prefix?: string
  suffix?: string
  title?: string
  value: number
  /**
   * The component width
   * @default 200
   */
  width?: number
  target?: number
}

const checkValidPrefix = (str: string) =>
  !str || (str.length < 3 && !/\//.test(str))

function RadialProgress({
  max = 100,
  value,
  color,
  width = 200,
  suffix = "",
  prefix = "",
  title = "",
  target
}: IRadialProgressProps) {
  const id: string = useId()
  const theme = useTheme()
  const fract: number = value / max

  const defaultColor: string = color || theme.series[0]
  const scaledColor = typeof color === "function" ? color(fract).css() : null
  const barColor: string = scaledColor ? scaledColor : defaultColor

  const angle: number = Math.PI * 2 * fract
  const lineThickness: number = width < 120 ? 2 : 3
  const dotRadius: number = width < 120 ? 3 : 6
  const sizeMultiplier: number = width < 120 ? 0.8 : 1
  const gradId: string = `grad-${id}`

  const isValidPrefix: boolean = checkValidPrefix(prefix)
  const isValidSuffix: boolean = checkValidPrefix(suffix)

  if (!isValidPrefix) {
    return <p>Invalid Prefix `{prefix}`.</p>
  }
  if (!isValidSuffix) {
    return <p>Invalid Suffix `{suffix}`</p>
  }

  const arcPath: string | null = arcBuilder()({
    innerRadius: width * 0.45 - lineThickness * 0.5,
    outerRadius: width * 0.45 + lineThickness * 0.5,
    startAngle: 0,
    endAngle: angle
  })

  const dotPos: { x: number; y: number } = {
    x: Math.sin(angle) * (width * 0.45),
    y: -Math.cos(angle) * (width * 0.45)
  }

  const targetAngle: number =
    target && !Number.isNaN(target) ? Math.PI * 2 * (target / max) : 0
  const targetPathPositions = [
    [
      Math.sin(targetAngle) * (width * 0.45 - lineThickness * 4),
      -Math.cos(targetAngle) * (width * 0.45 - lineThickness * 4)
    ],
    [
      Math.sin(targetAngle) * (width * 0.45 + lineThickness * 4),
      -Math.cos(targetAngle) * (width * 0.45 + lineThickness * 4)
    ]
  ]
  const targetPathData: string = `M ${targetPathPositions[0].join(
    " "
  )} L ${targetPathPositions[1].join(" ")}`

  const showDot: boolean = angle <= Math.PI * 2
  const showInnerBorder: boolean = width >= 120

  return (
    <ChartWrap style={{ width }}>
      <PlotWrap style={{ width, height: width }}>
        <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
          <g transform={`translate(${width / 2}, ${width / 2})`}>
            {showInnerBorder ? (
              <circle
                r={width * 0.3}
                stroke={"black"}
                strokeOpacity={0.5}
                cx={0}
                cy={0}
                fill={`url(#${gradId})`}
              />
            ) : (
              ""
            )}
            <circle
              r={width * 0.45}
              stroke={theme.colorScales.fg(0).alpha(0.5).css()}
              strokeDasharray={"2,4"}
              cx={0}
              cy={0}
              fill="none"
            />
            {arcPath ? <path fill={barColor} d={arcPath} /> : ""}
            {showDot ? (
              <circle
                r={dotRadius}
                fill={barColor}
                cx={dotPos.x}
                cy={dotPos.y}
              />
            ) : (
              ""
            )}
            {target && targetPathData ? (
              <path
                stroke={theme.palette.text.primary}
                strokeWidth={2}
                strokeDasharray={"1,2"}
                d={targetPathData}
              />
            ) : (
              ""
            )}
          </g>
          <GradientDefs>
            <radialGradient id={gradId}>
              <stop offset={0} stopColor={barColor} stopOpacity={0} />
              <stop offset={1} stopColor={barColor} stopOpacity={0.1} />
            </radialGradient>
          </GradientDefs>
        </svg>
        <CenterLabel
          color={theme.palette.text.primary}
          sizeMultiplier={sizeMultiplier}
        >
          {prefix && <span>{prefix}</span>}
          {value}
          {suffix && <span>{suffix}</span>}
        </CenterLabel>
      </PlotWrap>
      {title && (
        <TitleLabel>
          <Typography variant="h4">{title}</Typography>
        </TitleLabel>
      )}
    </ChartWrap>
  )
}

export default RadialProgress
